import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoginForm from "./LoginForm";
import RegisterForm from "./RegisterForm";
import { ArrowLeft, LogIn, UserPlus } from "lucide-react";

type Tab = "login" | "register";

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

export default function AuthPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  // ✅ Query là source of truth
  const tab: Tab = useMemo(() => {
    const q = (params.get("tab") || "").toLowerCase();
    return q === "register" ? "register" : "login";
  }, [params]);

  const setTab = (next: Tab) => {
    // giữ các query khác nếu có (ví dụ from=...)
    const nextParams = new URLSearchParams(params);
    nextParams.set("tab", next);
    setParams(nextParams, { replace: true });
  };

  return (
    <main className="h-screen overflow-hidden bg-brand-ivory text-brand-brown">
      {/* ===== BACKGROUND (subtle) ===== */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ivory via-brand-ivory to-brand-ivory" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/8 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-bl from-brand-brown/5 via-transparent to-transparent" />
      </div>

      {/* ===== SHELL ===== */}
      <div className="relative h-full flex flex-col">
        {/* ===== TOP BAR (compact) ===== */}
        <header className="h-14 flex items-center border-b border-brand-brown/10">
          <div className="mx-auto w-full max-w-6xl px-5 sm:px-8 flex items-center justify-between gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.55em] text-brand-brown/60 hover:text-brand-brown transition"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("auth.back")}
            </Link>

            <p className="hidden sm:block text-[10px] uppercase tracking-[0.62em] text-brand-brown/45">
              {t("auth.secureAccessTitle")}
            </p>
          </div>
        </header>

        {/* ===== CONTENT (no page scroll) ===== */}
        <section className="flex-1 min-h-0">
          <div className="mx-auto h-full w-full max-w-6xl px-5 sm:px-8 py-6">
            <div className="h-full min-h-0 grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              {/* ===== LEFT “POSTER” (compact, not long) ===== */}
              <aside className="hidden xl:block xl:col-span-5 min-h-0">
                <div className="h-full rounded-[26px] border border-brand-brown/10 bg-brand-ivory/70 backdrop-blur-md overflow-hidden relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-brown/6 via-transparent to-brand-gold/10" />
                  <div className="relative h-full p-10 flex flex-col justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
                        {t("auth.poster.privateAccount")}
                      </p>

                      <h1 className="mt-8 text-[58px] leading-[0.95] font-serif-lux uppercase tracking-[0.18em]">
                        OWL
                        <br />
                        DREAM
                      </h1>

                      <p className="mt-6 text-sm leading-[2.0] text-brand-brown/60 max-w-[34ch]">
                        {t("auth.poster.description")}
                      </p>

                      <div className="mt-8 flex items-center gap-4">
                        <span className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/45">
                          {t("footer.tagline")}
                        </span>
                        <span className="h-px w-14 bg-brand-brown/20" />
                        <span className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/45">
                          {t("auth.poster.secureAccess")}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-6">
                      <div className="rounded-full border border-brand-brown/10 bg-brand-ivory/70 px-4 py-2">
                        <span className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/55">
                          {t("auth.poster.encryptedSession")}
                        </span>
                      </div>

                      <div className="rounded-full border border-brand-brown/10 bg-brand-ivory/70 px-4 py-2">
                        <span className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/55">
                          {t("auth.poster.memberDrops")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </aside>

              {/* ===== RIGHT FORM (fixed height, internal scroll) ===== */}
              <div className="xl:col-span-7 min-h-0 flex items-center justify-center">
                <div className="w-full max-w-[520px] min-h-0">
                  {/* tabs (pill, compact) */}
                  <div className="rounded-[22px] border border-brand-brown/10 bg-brand-ivory/70 backdrop-blur-md p-2">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setTab("login")}
                        className={cn(
                          "h-10 rounded-[16px] transition",
                          "inline-flex items-center justify-center gap-2",
                          "text-[10px] uppercase tracking-[0.45em]",
                          tab === "login"
                            ? "bg-brand-brown text-brand-ivory"
                            : "text-brand-brown/55 hover:bg-brand-brown/5 hover:text-brand-brown",
                        )}
                        aria-label={t("auth.login")}
                      >
                        <LogIn className="w-4 h-4" />
                        {t("auth.login")}
                      </button>

                      <button
                        type="button"
                        onClick={() => setTab("register")}
                        className={cn(
                          "h-10 rounded-[16px] transition",
                          "inline-flex items-center justify-center gap-2",
                          "text-[10px] uppercase tracking-[0.45em]",
                          tab === "register"
                            ? "bg-brand-brown text-brand-ivory"
                            : "text-brand-brown/55 hover:bg-brand-brown/5 hover:text-brand-brown",
                        )}
                        aria-label={t("auth.register")}
                      >
                        <UserPlus className="w-4 h-4" />
                        {t("auth.register")}
                      </button>
                    </div>
                  </div>

                  {/* CARD: fixed viewport height + scroll inside */}
                  <div
                    className={cn(
                      "mt-4 rounded-[26px] border border-brand-brown/10 bg-brand-ivory/85 backdrop-blur-md",
                      "shadow-[0_30px_90px_rgba(43,31,26,0.10)] overflow-hidden",
                      "max-h-[calc(100vh-180px)]",
                    )}
                  >
                    {/* card header */}
                    <div className="px-6 sm:px-8 py-5 border-b border-brand-brown/10">
                      <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
                        {tab === "login"
                          ? t("auth.signInTitle")
                          : t("auth.createAccountTitle")}
                      </p>
                      <p className="mt-2 text-sm text-brand-brown/60 leading-relaxed">
                        {tab === "login"
                          ? t("auth.signInSubtitle")
                          : t("auth.createAccountSubtitle")}
                      </p>
                    </div>

                    {/* card body (scroll here) */}
                    <div className="px-6 sm:px-8 py-6 overflow-y-auto min-h-0">
                      <div className="relative">
                        <div
                          className={cn(
                            "transition-all duration-250",
                            tab === "login"
                              ? "opacity-100 translate-y-0"
                              : "pointer-events-none absolute inset-0 opacity-0 translate-y-2",
                          )}
                        >
                          <LoginForm />
                        </div>

                        <div
                          className={cn(
                            "transition-all duration-250",
                            tab === "register"
                              ? "opacity-100 translate-y-0"
                              : "pointer-events-none absolute inset-0 opacity-0 translate-y-2",
                          )}
                        >
                          <RegisterForm />
                        </div>
                      </div>

                      <div className="mt-6 flex items-center justify-between gap-4">
                        <p className="text-[9px] uppercase tracking-[0.45em] text-brand-brown/35">
                          {t("auth.secureAccess")}
                        </p>
                        <Link
                          to="/story"
                          className="text-[9px] uppercase tracking-[0.55em] text-brand-brown/45 hover:text-brand-brown transition"
                        >
                          {t("auth.readStory")}
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* mobile mini-note (tiny, not long) */}
                  <div className="xl:hidden mt-4 text-[10px] uppercase tracking-[0.55em] text-brand-brown/40">
                    {t("auth.bornFromNight")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== FOOTER (thin) ===== */}
        <footer className="h-12 border-t border-brand-brown/10">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 h-full flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/35">
              © 2025 OWL DREAM
            </p>
            <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/35">
              {t("footer.tagline")}
            </p>
          </div>
        </footer>
      </div>
    </main>
  );
}
